import {
  Button,
  ButtonGroup,
  Separator,
  Popover,
  useDisclosure,
} from "@chakra-ui/react";
import { t } from "i18next";
import { observer } from "mobx-react";
import { useState, useMemo, useEffect } from "react";
import { FilterTag } from "./FilterTag";
import { GlobalScoreFilter } from "./score-filters/GlobalScoreFilter";
import { CrashTypeScoreFilter } from "./score-filters/CrashTypeScoreFilter";
import { typedEntries, Score, ScoreEntry } from "@keita/lanepatrol-sdk";
import {
  isScoreFiltersEmpty,
  ScoreFilter,
  ScoreFilterType,
  ResultsScoreLimits,
  scoreFilterToString,
} from "../../../../map/models/MapFilters";
import { tScoreType } from "../../../../coding/utils/score-utils";
import { useStores } from "../../../../shared/stores/RootStore";
import { CustomPopover } from "../../../../shared/views/CustomPopover";
import { AddFilterButton } from "./AddFilterButton";
import { HlaLane } from "../../../../ra/models/HlaLane";

const HlaScoreFiltersBase: React.FC = () => {
  const { open, onToggle, onClose } = useDisclosure();
  const { filterStore, hlaStore } = useStores();

  const [filters, setFilters] = useState<Map<keyof Score, ScoreFilter>>(
    new Map(),
  );

  useEffect(() => {
    if (open) {
      setFilters(() => new Map(typedEntries(filterStore.hlaScoreFilters)));
    }
    //eslint-disable-next-line
  }, [open]);

  const limits = useMemo(() => {
    const limits = new Map<string, ResultsScoreLimits>();

    const processLane = (lane: HlaLane | null) => {
      if (lane == null || lane.score == null) return;

      for (const [scoreType, score] of typedEntries<keyof Score, ScoreEntry>(
        lane.score,
      )) {
        const limit = limits.get(scoreType)!;
        if (!limit) {
          limits.set(scoreType, { min: score.value, max: score.value });
        } else {
          if (limit.min > score.value) limit.min = score.value;
          if (limit.max < score.value) limit.max = score.value;
        }
      }
    };

    for (const way of hlaStore.ways) {
      processLane(way.frontFacingLane);
      processLane(way.backFacingLane);
    }
    return limits;
  }, [hlaStore.ways]);

  const handleChange = (key: keyof Score, value: ScoreFilter) => {
    setFilters((prev) => {
      prev.set(key, value);
      return new Map(prev);
    });
  };

  const handleClear = () => {
    setFilters(new Map());
  };

  const handleSubmit = () => {
    const activeFilters = new Map<keyof Score, ScoreFilter>();
    for (const [filterType, filterValue] of filters) {
      if (isFilterActive(filterValue, limits.get(filterType)!)) {
        activeFilters.set(filterType, filterValue);
      }
    }
    filterStore.setHlaScoreFilters(Object.fromEntries(activeFilters));
    onClose();
  };

  const removeActiveFilter = (crashType: keyof Score) => {
    const newFilters = { ...filterStore.hlaScoreFilters };
    delete newFilters[crashType];
    filterStore.setHlaScoreFilters(newFilters);
  };

  if (limits.size === 0) return null;

  return (
    <>
      <CustomPopover
        trigger={<AddFilterButton label="Score" onClick={onToggle} />}
        asChild={false}
        open={open}
        onClose={onClose}
        w={500}
        maxH="460px"
      >
        {open && (
          <>
            <Popover.Header>{t("map:filters.byScoreTitle")}</Popover.Header>
            <Popover.Body overflowY="auto" px={6}>
              <GlobalScoreFilter
                filter={filters.get("global")}
                setFilter={(value) => handleChange("global", value)}
                limits={limits}
              />
              <Separator mt={2} mb={8} />
              <CrashTypeScoreFilter
                filters={filters}
                setFilters={setFilters}
                limits={limits}
              />
            </Popover.Body>
            <Popover.Footer display="flex" justifyContent="flex-end">
              <ButtonGroup colorPalette={"primary"} size={"sm"}>
                <Button onClick={handleClear} disabled={!filters.size}>
                  {t("map:filters.clearButton")}
                </Button>
                <Button onClick={handleSubmit}>
                  {t("map:filters.submitButton")}
                </Button>
              </ButtonGroup>
            </Popover.Footer>
          </>
        )}
      </CustomPopover>

      {!isScoreFiltersEmpty(filterStore.hlaScoreFilters) &&
        Object.entries(filterStore.hlaScoreFilters).map(
          ([scoreType, filter], i) =>
            filter && (
              <FilterTag
                key={i}
                onClose={() => removeActiveFilter(scoreType as keyof Score)}
              >
                {`${tScoreType(
                  scoreType as keyof Score,
                )}: ${scoreFilterToString(filter)}`}
              </FilterTag>
            ),
        )}
    </>
  );
};

export const HlaScoreFilters = observer(HlaScoreFiltersBase);

const isFilterActive = (filter: ScoreFilter, limit: ResultsScoreLimits) => {
  if (filter.type === ScoreFilterType.RiskFactors && filter.riskFactors.length)
    return true;
  if (filter.type === ScoreFilterType.Limits && filter.limits.min !== limit.min)
    return true;
  if (filter.type === ScoreFilterType.Limits && filter.limits.max !== limit.max)
    return true;
  return false;
};
